/**
 * Shared date utilities for both client-public and client-admin.
 */

// Timestamp.AsObject type from google-protobuf
interface TimestampAsObject {
  seconds: number;
  nanos: number;
}

/**
 * Converts a UTC timestamp to a date string in EST timezone.
 * This ensures consistent date display across both admin and public clients.
 * @param timestamp - Timestamp.AsObject in UTC (from gRPC)
 * @returns Date string in YYYY-MM-DD format (in EST)
 */
export function timestampToDateString(
  timestamp: TimestampAsObject | undefined
): string {
  if (!timestamp) return "";

  // Convert timestamp to Date (UTC)
  const utcDate = new Date(
    timestamp.seconds * 1000 + timestamp.nanos / 1000000
  );

  // Format in EST timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(utcDate);
  const year = parts.find((p) => p.type === "year")?.value || "";
  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";

  return `${year}-${month}-${day}`;
}

/**
 * Converts a UTC timestamp to a time string in EST timezone (24-hour HH:MM).
 * Use this for display so admin and public client show the same time.
 * @param timestamp - Timestamp.AsObject in UTC (from gRPC)
 * @returns Time string in HH:MM format (24-hour, in EST)
 */
export function timestampToTimeString(
  timestamp: TimestampAsObject | undefined
): string {
  if (!timestamp) return "";

  const utcDate = new Date(
    timestamp.seconds * 1000 + timestamp.nanos / 1000000
  );

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(utcDate);
  const hour = parts.find((p) => p.type === "hour")?.value || "00";
  const minute = parts.find((p) => p.type === "minute")?.value || "00";
  return `${hour.padStart(2, "0")}:${minute}`;
}

/**
 * Converts a UTC timestamp to date and time strings in EST.
 * Single source of truth for "when" the event is – use this so admin and public share the same logic.
 * @param timestamp - Timestamp.AsObject in UTC (from gRPC)
 * @returns Object with date (YYYY-MM-DD) and time (HH:MM) in EST
 */
export function timestampToDateTime(
  timestamp: TimestampAsObject | undefined
): { date: string; time: string } {
  if (!timestamp) return { date: "", time: "" };
  return {
    date: timestampToDateString(timestamp),
    time: timestampToTimeString(timestamp),
  };
}

/**
 * Formats a date string for display (e.g., "January 22, 2026").
 * Parses YYYY-MM-DD format directly without timezone conversion.
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string (e.g., "January 22, 2026")
 */
export function formatDate(dateString: string): string {
  if (!dateString) return "";

  // Parse YYYY-MM-DD format directly
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    const date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10)
    );
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // Fallback for other formats
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
